import { useEffect } from 'react';
import type { IntegrationType } from '@yawi/types';
import {
  Mail,
  Smartphone,
  MessageSquare,
  Bell,
  CreditCard,
  Camera,
  Cloud,
  CheckCircle,
} from 'lucide-react';
import { typeInfo, providersByType } from '../providerConfig';
import type { StepProps } from '../types';

const typeIcons: Record<IntegrationType, React.ReactNode> = {
  EMAIL: <Mail className="w-6 h-6" />,
  SMS: <Smartphone className="w-6 h-6" />,
  WHATSAPP: <MessageSquare className="w-6 h-6" />,
  PUSH: <Bell className="w-6 h-6" />,
  PAYMENT: <CreditCard className="w-6 h-6" />,
  ACCESS: <Camera className="w-6 h-6" />,
  STORAGE: <Cloud className="w-6 h-6" />,
};

const types: IntegrationType[] = ['EMAIL', 'SMS', 'WHATSAPP', 'PUSH', 'PAYMENT', 'ACCESS', 'STORAGE'];

export function TypeStep({ data, onChange, onValidChange }: StepProps) {
  useEffect(() => {
    onValidChange(!!data.type);
  }, [data.type, onValidChange]);

  const handleSelect = (type: IntegrationType) => {
    const defaultProvider = providersByType[type][0].provider;
    onChange({
      type,
      provider: defaultProvider,
      config: {},
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          Selecciona el tipo de integración
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Elige el canal de comunicación o servicio que deseas configurar
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3">
        {types.map((type) => {
          const info = typeInfo[type];
          const isSelected = data.type === type;

          return (
            <button
              key={type}
              type="button"
              onClick={() => handleSelect(type)}
              className={`relative p-4 border-2 rounded-xl text-left transition-all ${
                isSelected
                  ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-lg ${
                  isSelected
                    ? 'bg-brand-100 dark:bg-brand-900/30 text-brand-600 dark:text-brand-400'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
                }`}>
                  {typeIcons[type]}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`font-semibold ${
                      isSelected
                        ? 'text-brand-700 dark:text-brand-300'
                        : 'text-gray-800 dark:text-white'
                    }`}>
                      {info.label}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                      {providersByType[type].length} proveedor{providersByType[type].length !== 1 ? 'es' : ''}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {info.description}
                  </p>
                </div>
                {isSelected && (
                  <CheckCircle className="w-5 h-5 text-brand-500 absolute top-4 right-4" />
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
